/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the folder (dossier) input/research
 * Mostly used in the website's header in order to switch from a folder to another
 */

// Imports
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

// Utils
import { fetchDossiers } from "../../utils/ApiCallConstructor";
import { ErrorForUsr, displayAlert, signOut } from '../../utils/Errors';

// Interfaces
import { Dossier } from '../../../interfaces/IDossiers';
import { AlertType } from "../../../interfaces/IUtils";
import { getDossierNameFormated } from "../../utils/Format";


export const FolderInput = (props) => {
    const navigate = useNavigate();

    const inHeader = props.inHeader || false; // is the input in the header

    const [dossiers, setDossiers] = useState([] as Array<Dossier>);
    const [displayList, setDisplayList] = useState(false);

    // get initial data
    useEffect(() => {

        // Get folders/dossiers from BACK or localstorage, otherwise, display error and handle session expiration
        fetchDossiers().then((fetchedDossiers) => setDossiers(fetchedDossiers))
            .catch((err: any) => {
                displayAlert({ message: err.error, type: AlertType.error });
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));
            });

        // Hide the folder listing if the user exits the input or the list
        const handleExitingFolderInput = (event) => {
            // if the focused element is neither the input nor the list of folder nor a <li> in list
            if (event.target !== folderInputEl.current && event.target !== searchedFolderEl.current && event.target.parentElement !== searchedFolderEl.current) {
                setDisplayList(false);
                setSearchedFolder('');
            }
        }


        document.addEventListener('click', handleExitingFolderInput);

        return () => {
            document.removeEventListener('click', handleExitingFolderInput);
        }
    }, [navigate]);


    // States about dossier
    const [dossier, setDossier] = useState(props.dossier || {} as Dossier); // Folder object
    const [searchedFolder, setSearchedFolder] = useState(''); // Folder input
    const [searchResults, setSearchResults] = useState<Array<Dossier>>([] as Array<Dossier>); // Array of Folder (based on search)
    const folderInputEl = useRef<HTMLInputElement>(null); // HTML Element for Folder selection (based on search)
    const searchedFolderEl = useRef<HTMLUListElement | null>(null); // HTML Element for Listing folder (based on search)

    // Display all dossiers at first
    useEffect(() => {
        setSearchResults(dossiers);
        setDisplayList(false);
    }, [dossiers]);


    // Get available folders based on the user keywords
    // each time the user types in something different
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (searchedFolder !== '') {
            // Browse each folders in order to get matching one
            const matchingDossiers = dossiers.filter((folder) => {
                return ( (folder.DOS_TITRE && folder.DOS_TITRE.toLowerCase().includes(searchedFolder.toLowerCase())) || (folder.DOS_CODE && (folder.DOS_CODE.toLowerCase().indexOf(searchedFolder.toLowerCase()) === 0)) || (folder.FIC_VAR && folder.FIC_VAR.length > 0 && folder.FIC_VAR[0] && folder.FIC_VAR[0].value?.toLowerCase().includes(searchedFolder.toLowerCase())) )
            });
            setSearchResults(matchingDossiers);
            // Display the ul element
            if (matchingDossiers.length > 0)
                setDisplayList(true);
        } else {
            // No matching folders
            setSearchResults(dossiers);
            setDisplayList(true);
        }
    }, [searchedFolder]);
    /* eslint-enable react-hooks/exhaustive-deps */


    const goToDossier = (folder: Dossier) => {
        // Process the dossier change
        if (window.location.pathname !== '/mon-dossier') {
            localStorage.setItem('currentFolder', JSON.stringify(folder));
            navigate(`/mon-dossier`, { state: { dossier: folder } })
        } else {
            setDossier(folder); // refresh
            localStorage.setItem('currentFolder', JSON.stringify(folder));
            props.onFolderChange(folder);
        }

    }

    const folderListKeyDownHandler = (event) => {
        if (event.key === "ArrowDown")
            (document.activeElement?.nextElementSibling as HTMLLIElement)?.focus();
        else if (event.key === "ArrowUp")
            if (document.activeElement?.previousSibling === null)
                (folderInputEl.current as HTMLInputElement).focus(); // focus the input
            else
                (document.activeElement?.previousSibling as HTMLLIElement)?.focus(); // focus previous matching li
        else if (event.key === "Enter")
            (document.activeElement as HTMLLIElement).click();
    }


    const folderListStyle = inHeader ? { marginTop: '2.6rem', borderRadius: '0px 0px 1.25rem 1.25rem' } : { marginTop: '6.1rem', borderRadius: '0px 0px 3rem 3rem' };

    // HTML Template
    return (
        <div className={`flex flex-col ${!inHeader ? 'w-4/5' : 'w-[92%]'} m-auto items-center ${props.className ? props.className : ''}`}>
            {props.welcome ? <h1 className="text-3xl mx-auto mb-12 ml text-center uppercase">Bienvenue dans votre Espace Client</h1> : <></>}
            <div className="flex flex-col w-full relative">
                {!inHeader && <FontAwesomeIcon className={`absolute cursor-pointer h-8 w-8 mx-8 my-8 ${inHeader ? 'hidden' : ''}`} icon={faMagnifyingGlass} />}
                <input
                    type="text"
                    placeholder={`${dossier.DOS_REF > 0 ? getDossierNameFormated(dossier) : 'Recherchez votre dossier'}`}
                    className={`${inHeader ? 'px2 pl-4 py-1 text-2xl' : 'px-20 py-4 text-4xl'} leading-4 focus-visible:outline-none border border-gray-300 text-gray-500 w-full`}
                    style={{ borderRadius: ` ${((searchedFolder !== dossier.DOS_TITRE) && searchResults.length > 0 && displayList) ? (inHeader ? '1.25rem 1.25rem 0px 0px' : '3rem 3rem 0px 0px') : (inHeader ? '2rem' : '3rem')}` }}
                    value={searchedFolder}
                    onChange={(e) => { setSearchedFolder(e.target.value); }}
                    ref={folderInputEl}
                    tabIndex={2}
                    onKeyDown={(e) => { if (e.key === "ArrowDown") (searchedFolderEl.current?.firstElementChild as HTMLLIElement).focus() }}
                    onClick={() => { if (searchResults.length > 0) setDisplayList(true) }} />


                {(searchedFolder !== dossier.DOS_TITRE) && searchResults.length > 0 && displayList && (
                    <ul id="dossiersList"
                        className={`absolute bg-white border border-gray-300 z-40 max-h-80 overflow-x-auto ${inHeader ? 'w-full pl-0' : 'w-4/5'}`}
                        ref={searchedFolderEl}
                        style={folderListStyle}>
                        {searchResults.map((folder, index) => (
                            <li tabIndex={4 + index} key={index} className="px-4 py-2 cursor-pointer text-lg hover:bg-gray-100"
                                onClick={() => goToDossier(folder)} onKeyDown={folderListKeyDownHandler}>
                                {getDossierNameFormated(folder)}
                            </li>
                        ))}
                        <div className="h-4"></div>
                    </ul>)}
            </div>
        </div>
    );
};

FolderInput.propTypes = {
    dossier: PropTypes.object,
    className: PropTypes.string,
    inHeader: PropTypes.bool,
    onFolderChange: PropTypes.func,
    welcome: PropTypes.bool
}